import { Uuid } from "@/domains/global/identifiers";
import { api } from "@/modules/api";
import { useQuery } from "@tanstack/react-query";
import { FC, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Button, Flex } from "@radix-ui/themes";
import { atlasRoutes } from "@/app/router/routes";
import { DateTime } from "luxon";
import Lowlight from "react-lowlight";
import styled from "@emotion/styled";

export const SessionsViewPage: FC = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId: Uuid }>();
  const [isDeleting, setIsDeleting] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);

  if (!sessionId) {
    return <div>Invalid ID</div>;
  }

  const result = useQuery({
    queryKey: ["session-by-id", sessionId],
    queryFn: () =>
      api.get("/internal/v1/sessions/{session_id}", {
        params: {
          path: {
            session_id: sessionId,
          },
        },
      }),
    staleTime: DateTime.fromObject({ minute: 1 }).toMillis(),
  });

  const handleDeleteSession = async () => {
    setIsDeleting(true);
    try {
      await api.delete("/internal/v1/sessions/{session_id}", {
        params: {
          path: {
            session_id: sessionId,
          },
        },
      });

      // Navigate back to sessions list after successful deletion
      navigate(atlasRoutes.sessions({}).path);
    } catch (err) {
      alert(`Failed to delete session: ${err instanceof Error ? err.message : String(err)}`);
      setIsDeleting(false);
      setShowConfirmDelete(false);
    }
  };

  if (result.isLoading) {
    return <div>Loading...</div>;
  }

  if (result.isError || !result.data) {
    return <div>Error: {result.error?.message ?? "Unknown error"}</div>;
  }

  return (
    <Container>
      <br />
      <ButtonGroup>
        <Button
          variant="outline"
          onClick={() => navigate(atlasRoutes.sessionEvents({ params: { sessionId } }).path)}
        >
          View Session Events
        </Button>

        {showConfirmDelete ? (
          <Flex gap="2">
            <Button color="red" disabled={isDeleting} onClick={handleDeleteSession}>
              {isDeleting ? "Deleting..." : "Confirm Delete"}
            </Button>
            <Button variant="soft" onClick={() => setShowConfirmDelete(false)}>
              Cancel
            </Button>
          </Flex>
        ) : (
          <Button color="red" variant="soft" onClick={() => setShowConfirmDelete(true)}>
            Delete Session
          </Button>
        )}
      </ButtonGroup>

      <br />
      <h1>View Session</h1>
      <Lowlight language="json" value={JSON.stringify(result.data, null, 2)} markers={[]} />
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  padding: 20px;
`;

const ButtonGroup = styled(Flex)`
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;
